import { isEqual } from "lodash-es";
import type { FC } from "react";

import type { ChecklistRequirement, RequirementStatus, Task } from "@recobra/schemas/task";

import { TaskMetadataFieldActions } from "../task-details";
import type { EditableTaskField, TaskMetadataEditor } from "../task-details";
import { RequestedDocumentsSection } from "../task-form";
import { ChecklistRowGroup } from "./ChecklistRowGroup";
import type { ChecklistGroupSpec, ChecklistRowEntry } from "./checklistRowGrouping";
import type { ChecklistRowDestination } from "./checklistRowLink";
import { RequirementChecklistItem } from "./RequirementChecklistItem";

export interface RequirementGroupSpec extends ChecklistGroupSpec {
  title: string;
  defaultOpen?: boolean;
  editableField?: EditableTaskField;
  /** The group is satisfied once this many rows are satisfied and not waived; absent means every row. */
  minimumSatisfied?: number;
}

export interface RequirementChecklistHandlers {
  onSatisfy: (requirementKey: string, satisfied: boolean) => void;
  onWaive: (requirementKey: string, waived: boolean) => void;
  onNavigate?: () => void;
}

export const ChecklistRowRenderer: FC<{
  row: ChecklistRowEntry<RequirementGroupSpec>;
  task: Task;
  canWaive: boolean;
  disabled: boolean;
  handlers: RequirementChecklistHandlers;
  linkFor: (requirement: ChecklistRequirement) => ChecklistRowDestination | null;
  editor?: TaskMetadataEditor;
}> = ({ row, task, canWaive, disabled, handlers, linkFor, editor }) => {
  if (row.type === "item") {
    const { requirement, satisfied, waived } = row.status;

    return (
      <RequirementChecklistItem
        requirement={requirement}
        satisfied={satisfied}
        waived={waived}
        task={task}
        canWaive={canWaive}
        onSatisfy={handlers.onSatisfy}
        onWaive={handlers.onWaive}
        disabled={disabled}
        link={linkFor(requirement)}
        onNavigate={handlers.onNavigate}
      />
    );
  }

  const { spec, statuses } = row;
  const groupSatisfied = isGroupSatisfied(statuses, spec.minimumSatisfied);
  const links = statuses.map(({ requirement }) => linkFor(requirement));
  const sharedLink = links.every((link) => isEqual(link, links[0])) ? links[0] : null;
  const link = groupSatisfied ? null : sharedLink;
  const editableField = editor?.canEdit ? spec.editableField : undefined;
  const isEditing = !!editableField && editor?.editing === editableField;

  return (
    <ChecklistRowGroup
      title={spec.title}
      satisfied={groupSatisfied}
      link={isEditing ? null : link}
      disabled={disabled}
      defaultOpen={spec.defaultOpen}
      forceOpen={isEditing}
      actions={
        editableField && editor ? (
          <TaskMetadataFieldActions field={editableField} editor={editor} />
        ) : null
      }
      onNavigate={handlers.onNavigate}
    >
      {isEditing && editor ? (
        <li>
          <RequestedDocumentsSection
            form={editor.form}
            contextType={task.contextType}
            contextId={task.contextId}
            hideLabel
          />
        </li>
      ) : (
        statuses.map((status, index) => (
          <RequirementChecklistItem
            key={status.requirement.key}
            requirement={status.requirement}
            satisfied={status.satisfied}
            waived={status.waived}
            task={task}
            canWaive={canWaive}
            onSatisfy={handlers.onSatisfy}
            onWaive={handlers.onWaive}
            disabled={disabled}
            link={sharedLink ? null : links[index]}
            onNavigate={handlers.onNavigate}
          />
        ))
      )}
    </ChecklistRowGroup>
  );
};

const isGroupSatisfied = (
  statuses: readonly RequirementStatus[],
  minimumSatisfied: number | undefined,
): boolean =>
  minimumSatisfied === undefined
    ? statuses.every((status) => status.satisfied)
    : statuses.filter(({ satisfied, waived }) => satisfied && !waived).length >= minimumSatisfied;
